<template>
    <section class="max-w-3xl mx-auto px-4 py-10 text-gray-800 overflow-auto">
      <h1 class="text-3xl font-bold mb-6">Privacy Policy</h1>
      <p class="text-sm text-gray-500 mb-10">Effective Date: April 19, 2025</p>

      <div v-for="section in privacySections" :key="section.title" class="mb-8">
        <h2 class="text-xl font-semibold mb-2">{{ section.title }}</h2>
        <div v-html="section.content" class="text-base space-y-3"></div>
      </div>
    </section>
  </template>

<script setup>
const privacySections = [
  {
    title: '1. Information We Collect',
    content: `
        <ul class="list-disc pl-5 space-y-1">
          <li><strong>Account Information:</strong> When you sign up, we may collect your email address, username, password, and language preferences.</li>
          <li><strong>Usage Data:</strong> We collect data on how you interact with the app, including conversations, message patterns, feedback responses, and session durations.</li>
          <li><strong>Device & Technical Information:</strong> This includes your IP address, browser type, device type, operating system, and app version.</li>
          <li><strong>Communication Data:</strong> If you contact us, we may keep records of your communication, including support requests and feedback.</li>
        </ul>
      `
  },
  {
    title: '2. How We Use Your Information',
    content: `
        <ul class="list-disc pl-5 space-y-1">
          <li>Provide, maintain, and improve the Service.</li>
          <li>Offer personalized feedback, corrections, and language tips.</li>
          <li>Analyze usage trends to enhance the learning experience.</li>
          <li>Detect, prevent, and address technical issues or misuse.</li>
          <li>Communicate with you about updates, support, and promotional content (if you opt in).</li>
        </ul>
      `
  },
  {
    title: '3. AI Interactions and User Content',
    content: `
        <p>Your interactions with our AI—including your messages, questions, and responses—are stored temporarily and may be analyzed to improve our models and services. These interactions may be anonymized and aggregated for research and development.</p>
        <p>We do <strong>not</strong> use your conversations for advertising purposes or sell your personal data.</p>
      `
  },
  {
    title: '4. Data Sharing and Disclosure',
    content: `
        <p>We do <strong>not</strong> sell your personal data. We may share your data only in the following cases:</p>
        <ul class="list-disc pl-5 space-y-1">
          <li><strong>Service Providers:</strong> With trusted third-party vendors who help us operate the Service (e.g., cloud storage, analytics).</li>
          <li><strong>Legal Compliance:</strong> If required by law or to respond to valid legal requests.</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or asset sale, your data may be transferred to a new entity.</li>
        </ul>
      `
  },
  {
    title: '5. Data Storage and Security',
    content: `
        <ul class="list-disc pl-5 space-y-1">
          <li>Your data is stored securely using encrypted cloud infrastructure.</li>
          <li>We implement industry-standard safeguards to protect against unauthorized access, disclosure, or destruction.</li>
          <li>While we strive to protect your data, no method of transmission or storage is 100% secure.</li>
        </ul>
      `
  },
  {
    title: '6. Your Rights and Choices',
    content: `
        <p>Depending on your location, you may have the right to:</p>
        <ul class="list-disc pl-5 space-y-1">
          <li>Access or correct your personal information.</li>
          <li>Request deletion of your data.</li>
          <li>Withdraw consent for data processing.</li>
          <li>Object to certain types of data use.</li>
        </ul>
        <p>To make any of these requests, please contact us at [insert contact email or form].</p>
      `
  },
  {
    title: '7. Children’s Privacy',
    content: `
        <p>PapoteAI is not intended for children under the age of 13 (or the minimum age in your jurisdiction). We do not knowingly collect personal data from children without parental consent.</p>
      `
  },
  {
    title: '8. International Users',
    content: `
        <p>If you are accessing the Service from outside the country where we operate, please note that your information may be transferred to, stored, and processed in that country. By using the Service, you consent to this transfer.</p>
      `
  },
  {
    title: '9. Changes to This Privacy Policy',
    content: `
        <p>We may update this Privacy Policy from time to time. If we make material changes, we will notify you through the Service or by other means. The “Effective Date” at the top will always indicate the latest revision.</p>
      `
  },
  {
    title: '10. Contact Us',
    content: `
        <p>If you have any questions or concerns about this Privacy Policy, you can reach us at:</p>
        <p>📧 [insert contact email]</p>
      `
  }
]
</script>
